import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api/client.js';
import type { PracticeDaysResponse, ProfileResponse } from '../../lib/api/types.js';
import { Avatar } from '../../ui/primitives/Avatar.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { OfflineState } from '../../ui/components/OfflineState/OfflineState.tsx';
import { Editorial } from './directions/Editorial.tsx';
import { FollowButton } from './FollowButton.tsx';
import styles from './profile.module.css';

/**
 * `/perfil` and `/perfil/:userId` — "was any of this worth it?"
 *
 * THE OTHER HALF OF THE RANKING, and deliberately its opposite. The week
 * resets so that somebody who subscribed in March can still win one in
 * September. This never resets and never falls, so that eleven months in, a
 * learner whose weekly rank is thoroughly average still has an answer.
 *
 * ONE SCREEN FOR BOTH, because the server sends one shape for both. The
 * private figures — the freeze stock, whether you practised today — are simply
 * absent from a stranger's response, so this renders what it was given rather
 * than deciding a second time who may see what. A second component for
 * "someone else's profile" would be the one that eventually shows too much.
 *
 * THE ID IN THE PATH IS THE PRODUCT'S FIRST. Every other route here names
 * nothing, because a learner has one position and the server owns it. A
 * profile is the exception that proves the rule: it is not the learner's own
 * place in the book, it is ANOTHER PERSON, and a person is precisely the kind
 * of thing worth sending to somebody in a group chat.
 *
 * ----------------------------------------------------------------------------
 *
 * THIS FILE IS THE ROUTE, NOT THE SCREEN.
 *
 * What the Profile LOOKS like now lives in `directions/Editorial.tsx`, which
 * is one of six compositions built against this same data and compared side by
 * side in `dev/profile-exploration.html`. The split is deliberate and it is not
 * indirection for its own sake: a layout that can be swapped in one import is a
 * layout that can be argued with, and the other five stay in the tree precisely
 * so the next person to disagree with this one has something to disagree from.
 *
 * WHAT STAYS HERE IS EVERYTHING A DIRECTION MUST NOT DECIDE: which learner is
 * being read, what happens while the answer is in flight, what happens when it
 * never comes, and where the two controls lead. A direction receives a profile
 * that has already arrived and renders it.
 *
 * THE WEEK IS FETCHED HERE TOO, AND ONLY WHEN IT IS YOURS. `practice-days` is
 * session-scoped with no id in the path — asking for it while reading someone
 * else's profile would return YOUR week and put it on THEIR page, so the
 * request is gated on `you` rather than filtered afterwards.
 */
export function ProfilePage() {
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();

  const {
    status,
    data: profile,
    showLoading,
    reload,
    setData,
  } = useAsyncData<ProfileResponse | null>(() => api.profile(userId), null, [userId]);

  /*
   * The learner's own zone is SENT rather than inferred: a day boundary is a
   * fact about where the person is sitting, and the server has only an IP
   * address and its own clock to guess with (see `api.practiceDays`).
   */
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: week } = useAsyncData<PracticeDaysResponse | null>(
    () => (profile?.you ? api.practiceDays(zone) : Promise.resolve(null)),
    null,
    [zone, profile?.you],
  );

  if (status === 'loading') {
    return showLoading ? <LoadingState label="Carregando o perfil" /> : null;
  }

  if (status === 'unavailable' || !profile) {
    return (
      <div className={styles.page}>
        <OfflineState
          title="Não foi possível carregar o perfil"
          onRetry={() => reload({ force: true })}
        />
      </div>
    );
  }

  /*
    A LEARNER WHO KEEPS THEIR FIGURES TO THEMSELVES, answered here rather than
    inside a direction. The server sends a shape with no figures on it at all
    (`/conta` → "Quem vê seu perfil"), so this is not a composition rendering
    less: it is the page saying what it was told, and the reason no direction
    has to decide a second time what a stranger may read.

    THE PERSON IS STILL HERE, and so is the one thing this page is for:
    following them. A private profile is not a closed door — the name and the
    picture are the same two things a ranking row and an invite link already
    carry, and the follow is what the reader came to do.
  */
  if (profile.private) {
    return (
      <div className={styles.page}>
        <section className={styles.privateProfile} data-testid="private-profile">
          <Avatar name={profile.learner.name} image={profile.learner.image} size="lg" />

          <div className={styles.privateWho}>
            <Text as="h1" variant="heading">
              {profile.learner.name}
            </Text>
            <Text as="p" variant="body" tone="muted">
              Este perfil é privado. Os números de {profile.learner.name} ficam só com{' '}
              {profile.learner.name}.
            </Text>
          </div>

          <FollowButton
            userId={profile.learner.id}
            following={profile.following}
            onChange={(following) =>
              setData((current) => (current ? { ...current, following } : current))
            }
          />
        </section>
      </div>
    );
  }

  /* The server sends the window most recent first; every strip in the product
     fills left to right, which is the one direction § 9 lets progress grow. */
  const weekDays = week ? [...week.days].reverse() : [];

  return (
    <Editorial
      profile={profile}
      weekDays={weekDays}
      onFollowChange={(following) =>
        setData((current) => (current ? { ...current, following } : current))
      }
      onOpenSettings={() => navigate('/conta')}
    />
  );
}
